import { logger } from "~/utils/logging";

import { MediaFileType, MediaMimeTypeMap } from "./types";
import { MediaError } from "./errors";

import type { MediaElement } from "./types";
import type { MediaElementState } from "./state";

/**
 * Extracts properties from a video or audio element for use
 * in component state.
 */
export function getMediaElementProperties(mediaElement: MediaElement) {
  const keys: (keyof MediaElementState)[] = [
    "error",
    "networkState",
    "buffered",
    "readyState",
    "currentTime",
    "duration",
    "paused",
    "playbackRate",
    "ended",
    "volume",
    "muted",
  ];

  return keys.reduce((properties, key) => {
    if (mediaElement) {
      // @ts-expect-error
      properties[key] = mediaElement[key];
    }
    return properties;
  }, {} as MediaElementState);
}

/**
 * Determines the mime type of the media source
 */
export function getMimeTypeFromUrl(src: string) {
  const ext = src.match(/[^/.\s]+$/)?.[0].toUpperCase() as MediaFileType;
  return (
    MediaMimeTypeMap.get(ext) ?? MediaMimeTypeMap.get(MediaFileType.M3U8) ?? ""
  );
}

/**
 * Loads the media file into the player element.
 */
export async function load(
  mediaElement: MediaElement,
  props: { src?: string },
) {
  const { src } = props;

  if (!src || !src.length) {
    logger.error("No source given for media file");
    return null;
  }

  return import("@mux/playback-core").then(
    ({ Hls, ExtensionMimeTypeMap, setupHls, loadMedia }) => {
      const hls = setupHls({ type: ExtensionMimeTypeMap.M3U8 }, mediaElement);

      // An hls instance is returned if it is required to
      // play the media. It will be `undefined` if the
      // device supports http live streaming without MSE.
      if (hls) {
        hls.on(Hls.Events.MANIFEST_PARSED, (e, data) => {
          logger.debug(e, data);
        });

        hls.on(Hls.Events.ERROR, (e, data) => {
          if (!data.fatal) return;

          const errorCodeMap: Record<string, number> = {
            [Hls.ErrorTypes.NETWORK_ERROR]: MediaError.MEDIA_ERR_NETWORK,
            [Hls.ErrorTypes.MEDIA_ERROR]: MediaError.MEDIA_ERR_DECODE,
          };

          const code = errorCodeMap[data.type];
          const error = new MediaError("HLS Error", code);

          error.fatal = data.fatal;
          error.data = data;

          logger.error("HLS Error", error, {
            type: data.type,
            details: data.details,
            url: data.url,
            stats: JSON.stringify(data.stats),
            code,
          });
        });

        // Associate the src and media element with the hls
        // instance
        hls.loadSource(src);
        hls.attachMedia(mediaElement);
      }

      // Load the media, either via hls or native
      loadMedia({ src }, mediaElement, hls);

      return hls ?? null;
    },
  );
}
